import React, { useEffect, useMemo, useState } from 'react';
import { useAppContext } from 'services/context';
import { DEFAULT_QC_FAILED_CHECKS } from 'components/QCViewer/constants';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Grid,
  TextField,
  Typography,
} from '@mui/material';

import { DefaultDialog } from 'components/Modals/DefaultDialog';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import { useSelector } from 'react-redux';
import { currentUserEmailSelector } from 'store/slices/userDetailsSlice';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { serializeDataForCheckboxes } from 'components/utilities/gridDataSerializers';
import { useQCPassFailModalHookStyles } from 'components/Modals/styles';
import { ActiveOrderNotes } from 'components/Modals/QCPassFailModalHook/ActiveOrderNotes';
import { SimpleSlideView } from 'components/ImageViewer/SimpleSlideImageView';
import { QCPassFailModalActions } from 'components/Modals/QCPassFailModal/QCPassFailModalActions';
import { FailFormInputs } from 'components/Modals/QCPassFailModal/FailFormInputs';
import { SpecialInstructionsCheckboxes } from 'components/Modals/QCPassFailModal/SpecialInstructionsCheckboxes';
import { QCSlideInfoFields } from 'components/Modals/QCPassFailModal/QCSlideInfoFields';
import { useQCPassFailSubmit } from 'components/Modals/QCPassFailModal/hooks';
import { prepareUtilsVariables } from 'components/Modals/QCPassFailModal/utilities';

export const QCPassFailModal = ({
                                  slide,
                                  handleQCReviewed,
                                  isSlideLoading,
                                  handleNextSlide,
                                  handlePreviousSlide,
                                  selectedRows,
                                  getNextRow,
                                }) => {
  const [specialInstructionsCheckboxes, setSpecialInstructionsCheckboxes] =
    useState(null);
  const { classes } = useQCPassFailModalHookStyles();
  const { modalQCPassFailOpen, setModalQCPassFailOpen } = useAppContext();

  const email = useSelector(currentUserEmailSelector);
  const [isAccordionExpanded, setIsAccordionExpanded] = useState(true);
  const [isActiveOrderNotes, setIsActiveOrderNotes] = useState(false);
  const [isActiveOrderNotesChecked, setIsActiveOrderNotesChecked] =
    useState(false);
  const [qcChecks, setQCChecks] = useState(DEFAULT_QC_FAILED_CHECKS);
  const [currentQcScoreDisplay, setCurrentQcScoreDisplay] = useState(null);
  const { setInternalNotes, setExternalComment, isSubmitting, handleSubmit } =
    useQCPassFailSubmit(
      slide,
      qcChecks,
      handleQCReviewed,
      selectedRows,
      setModalQCPassFailOpen,
      getNextRow,
    );

  const isSpecialInstructionsChecked = useMemo(
    () =>
      specialInstructionsCheckboxes?.every(
        (specialInstruction) => specialInstruction.checked,
      ),
    [specialInstructionsCheckboxes],
  );

  useEffect(() => {
    if (!slide?.order) return;

    setSpecialInstructionsCheckboxes(serializeDataForCheckboxes(slide.order));
  }, [slide]);

  const handleTextChange = (name) => (event) => {
    const mapInputNameToSetter = {
      external_comment: setExternalComment,
      internal_notes: setInternalNotes,
    };

    const setter = mapInputNameToSetter[name];
    setter(event.target.value);
  };

  if (!(slide && modalQCPassFailOpen)) {
    return null;
  }

  const toggleAccordion = () => {
    setIsAccordionExpanded((prev) => !prev);
  };

  const {
    isGLP,
    ROI,
    modalSlideID,
    isShowModelOverlayEnabled,
    slideDetailsTitle,
    specialInstructionsAccordionTitle,
  } = prepareUtilsVariables(slide, currentQcScoreDisplay);

  const renderDialogActions = () => (
    <QCPassFailModalActions
      classes={classes}
      handleNextSlide={handleNextSlide}
      handlePreviousSlide={handlePreviousSlide}
      slide={slide}
      qcChecks={qcChecks}
      selectedRows={selectedRows}
      handleSubmit={handleSubmit}
      isSubmitting={isSubmitting}
      isSpecialInstructionsChecked={isSpecialInstructionsChecked}
      isActiveOrderNotes={isActiveOrderNotes}
      isActiveOrderNotesChecked={isActiveOrderNotesChecked}
    />
  );

  return (
    <>
      {!isSlideLoading && (
        <DefaultDialog
          open={modalQCPassFailOpen}
          onClose={() => setModalQCPassFailOpen(prev => !prev)}
          actions={renderDialogActions}
          title={slide.name}
          className={classes.dialog}
        >
          <Box width={'90vw'} height={'80vh'}>
            <Grid
              container
              spacing={1}
              className={classes.dialogContentWrapper}
            >
              <Grid item xs={8}>
                <SimpleSlideView
                  slide={slide}
                  id={modalSlideID}
                  showNavigator
                  showOverlay={isShowModelOverlayEnabled}
                  scoreToDisplay={currentQcScoreDisplay}
                  isQCViewer
                />
              </Grid>
              <Grid item xs={4} className={classes.qcReviewSidebarWrapper}>
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>{slideDetailsTitle}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <QCSlideInfoFields slide={slide} />
                  </AccordionDetails>
                </Accordion>
                <Box my={2}>
                  <Divider />
                </Box>
                <Grid item xs={12}>
                  <Typography
                    color={'inherit'}
                    marginBottom={1}
                    variant={'subtitle1'}
                  >
                    Quality Control Review by {email}
                  </Typography>
                  <ActiveOrderNotes
                    slide={slide}
                    setIsActiveOrderNotes={setIsActiveOrderNotes}
                    isActiveOrderNotesChecked={isActiveOrderNotesChecked}
                    setIsActiveOrderNotesChecked={setIsActiveOrderNotesChecked}
                    classes={classes}
                  />
                  {ROI ? (
                    <Accordion>
                      <AccordionSummary
                        onClick={toggleAccordion}
                        expandIcon={<ExpandMoreIcon />}
                      >
                        <Typography>Region of Interest</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Typography>{ROI}</Typography>
                      </AccordionDetails>
                    </Accordion>
                  ) : null}
                  {specialInstructionsCheckboxes?.length ? (
                    <Accordion
                      expanded={isAccordionExpanded}
                      className={classes.accordion}
                    >
                      <AccordionSummary
                        onClick={toggleAccordion}
                        expandIcon={<ExpandMoreIcon />}
                      >
                        <Typography>
                          {specialInstructionsAccordionTitle}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        {/* {renderSpecialInstructionsCheckboxes(slide.order)} */}
                        <SpecialInstructionsCheckboxes
                          classes={classes}
                          isSpecialInstructionsChecked={
                            isSpecialInstructionsChecked
                          }
                          specialInstructionsCheckboxes={
                            specialInstructionsCheckboxes
                          }
                          setSpecialInstructionsCheckboxes={
                            setSpecialInstructionsCheckboxes
                          }
                        />
                      </AccordionDetails>
                    </Accordion>
                  ) : null}
                  <FailFormInputs
                    classes={classes}
                    slide={slide}
                    qcChecks={qcChecks}
                    setQCChecks={setQCChecks}
                    isGLP={isGLP}
                    currentQcScoreDisplay={currentQcScoreDisplay}
                    setCurrentQcScoreDisplay={setCurrentQcScoreDisplay}
                  />
                  <TextField
                    id="standard-multiline-flexible"
                    label="Internal Slide Notes / Change Reason"
                    marginTop={1}
                    placeholder="Internal Slide Notes / Change Reason"
                    fullWidth
                    multiline
                    defaultValue={slide.internal_notes}
                    onChange={handleTextChange('internal_notes')}
                    className={classes.textField}
                  />
                  <TextField
                    id="standard-multiline-flexible"
                    label="External Slide Comment"
                    marginTop={1}
                    placeholder="External Slide Comment"
                    fullWidth
                    multiline
                    onChange={handleTextChange('external_comment')}
                    className={classes.textField}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </DefaultDialog>
      )}
    </>
  );
};
